import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent_given: false,
    marketing_opt_in: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_given) {
      toast.error('Please consent to the processing of your personal information.');
      return;
    }
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '', consent_given: false, marketing_opt_in: false });
    } catch (error) {
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Contact K&K Medical and Dental. Umzinto: 039 974 1570 | Scottburgh: 039 315 5041. WhatsApp 082 895 1916 or email info@kkmedical.co.za."
        keywords="contact doctor Umzinto, contact doctor Scottburgh, K&K Medical phone number, clinic address Umzinto, clinic address Scottburgh, book appointment doctor KZN"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6 animate-fade-in-up">
            Get in Touch
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6 animate-fade-in-up animation-delay-100">
            We're Here to Help
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            Have questions or need to book a surgical appointment? Reach out to us through any of the channels below.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden animate-fade-in-up">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
                
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="bg-teal-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-slate-600 mb-6">We'll get back to you as soon as possible.</p>
                    <Button 
                      onClick={() => setIsSubmitted(false)}
                      className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="rounded-xl bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className="rounded-xl bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                          data-testid="input-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="082 123 4567"
                        className="rounded-xl bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-700 font-medium">Your Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        rows={5}
                        className="rounded-xl bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                        data-testid="input-message"
                      />
                    </div>

                    {/* POPIA Consent */}
                    <div className="space-y-3 border-t border-slate-200 pt-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="consent_given"
                          name="consent_given"
                          checked={formData.consent_given}
                          onChange={handleChange}
                          className="mt-1 rounded border-slate-300"
                          required
                          data-testid="input-consent"
                        />
                        <label htmlFor="consent_given" className="text-sm text-slate-600 leading-relaxed">
                          I consent to K&K Medical and Dental collecting and processing my personal information to respond to this enquiry, in accordance with the <Link to="/privacy" className="text-teal-600 underline hover:text-teal-700">Privacy Notice (POPIA)</Link>. *
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="marketing_opt_in"
                          name="marketing_opt_in"
                          checked={formData.marketing_opt_in}
                          onChange={handleChange}
                          className="mt-1 rounded border-slate-300"
                          data-testid="input-marketing"
                        />
                        <label htmlFor="marketing_opt_in" className="text-sm text-slate-600 leading-relaxed">
                          I would like to receive health tips, promotions, and updates from K&K Medical and Dental via email or WhatsApp. I understand I can unsubscribe at any time.
                        </label>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 justify-center">
                          <Send className="w-5 h-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6 animate-fade-in-up animation-delay-200">
              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
                  <div className="space-y-6">
                    <a 
                      href="tel:0399741570" 
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-teal-50 transition-colors duration-200 group"
                      data-testid="contact-phone-link"
                    >
                      <div className="bg-teal-100 group-hover:bg-teal-200 rounded-full p-3 transition-colors duration-200">
                        <Phone className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Umzinto</p>
                        <p className="text-slate-900 font-semibold text-lg">039 974 1570</p>
                      </div>
                    </a>

                    <a 
                      href="tel:0393155041" 
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors duration-200 group"
                      data-testid="contact-phone-scottburgh-link"
                    >
                      <div className="bg-blue-100 group-hover:bg-blue-200 rounded-full p-3 transition-colors duration-200">
                        <Phone className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Scottburgh</p>
                        <p className="text-slate-900 font-semibold text-lg">039 315 5041</p>
                      </div>
                    </a>
                    
                    <a 
                      href="https://wa.me/27828951916" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-green-50 transition-colors duration-200 group"
                      data-testid="contact-whatsapp-link"
                    >
                      <div className="bg-green-100 group-hover:bg-green-200 rounded-full p-3 transition-colors duration-200">
                        <MessageCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">WhatsApp</p>
                        <p className="text-slate-900 font-semibold text-lg">082 895 1916</p>
                      </div>
                    </a>
                    
                    <a 
                      href="mailto:info@kkmedical.co.za" 
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors duration-200 group"
                      data-testid="contact-email-link"
                    >
                      <div className="bg-blue-100 group-hover:bg-blue-200 rounded-full p-3 transition-colors duration-200">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">Email</p>
                        <p className="text-slate-900 font-semibold text-lg">info@kkmedical.co.za</p>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Locations & Hours</h3>
                  
                  {/* Umzinto */}
                  <div className="mb-6 pb-6 border-b border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold">Main Branch</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-3 notranslate">K&K Medical & Dental — Umzinto</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-teal-100 rounded-full p-2 flex-shrink-0 mt-0.5">
                          <MapPin className="w-4 h-4 text-teal-600" />
                        </div>
                        <p className="text-slate-600 text-sm notranslate">
                          Lot 293 Nelson Mandela Road,<br />
                          Umzinto, KZN, South Africa
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-teal-100 rounded-full p-2 flex-shrink-0 mt-0.5">
                          <Clock className="w-4 h-4 text-teal-600" />
                        </div>
                        <div className="text-slate-600 text-sm">
                          <p>Mon - Fri: 8:00 AM - 5:00 PM</p>
                          <p>Saturday: 8:00 AM - 2:00 PM</p>
                          <p>Sun & Holidays: Closed</p>
                          <p className="text-teal-700 font-medium mt-1">GP: 10:00 AM - 5:00 PM (Mon-Fri) | 8:00 AM - 2:00 PM (Sat)</p>
                          <p className="text-amber-600 text-xs italic mt-0.5">*GP hours subject to change without prior notice</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">Services: Medical, Surgical, Insurance & Medicals, IV Infusions (Limited). No Dental or Specialist.</p>
                    <Button 
                      asChild
                      className="mt-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5 shadow-md hover:shadow-lg transition-all duration-300"
                      data-testid="get-directions-umzinto-btn"
                    >
                      <a 
                        href="https://www.google.com/maps/dir/?api=1&destination=-30.3096049,30.6633522&destination_place_id=ChIJMUVpO3Q59h4R2uVVVUP1kmA" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Get Directions
                      </a>
                    </Button>
                  </div>

                  {/* Scottburgh */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">New Branch</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-3 notranslate">K&K Medical & Dental — Scottburgh</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 mt-0.5">
                          <MapPin className="w-4 h-4 text-blue-600" />
                        </div>
                        <p className="text-slate-600 text-sm notranslate">
                          Scott Health Medical Center,<br />
                          41 Airth Street,<br />
                          Scottburgh, KZN
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 mt-0.5">
                          <Clock className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="text-slate-600 text-sm">
                          <p>Mon - Fri: 8:00 AM - 5:00 PM</p>
                          <p>Saturday: 8:00 AM - 2:00 PM</p>
                          <p>Sun & Holidays: Closed</p>
                          <p className="text-blue-700 font-medium mt-1">GP: 8:00 AM - 10:00 AM (Mon-Fri)</p>
                          <p className="text-amber-600 text-xs italic mt-0.5">*GP hours subject to change without prior notice</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">Services: All services including Dental & Specialist Services.</p>
                    <Button 
                      asChild
                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 shadow-md hover:shadow-lg transition-all duration-300"
                      data-testid="get-directions-scottburgh-btn"
                    >
                      <a 
                        href="https://www.google.com/maps/dir/?api=1&destination=-30.2861122,30.7508128" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Get Directions
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Find Us</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Our Locations
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 notranslate text-center">Umzinto Branch</h3>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100" data-testid="map-umzinto">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3444.4538888895986!2d30.660777275248833!3d-30.30960487479112!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef639743b694531%3A0x6092f5435555e5da!2sK%20and%20K%20Medical%20and%20Dental%20(Umzinto)!5e0!3m2!1sen!2sza!4v1777408413521!5m2!1sen!2sza"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="K&K Medical - Umzinto Location"
                />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 notranslate text-center">Scottburgh Branch</h3>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100" data-testid="map-scottburgh">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13781.116818475817!2d30.732788355419913!3d-30.286112199999987!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef637d043db8fc5%3A0x1fd80926d2642b49!2sK%20and%20K%20Medical%20and%20Dental%20-%20Scottburgh!5e0!3m2!1sen!2sza!4v1777408488726!5m2!1sen!2sza"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="K&K Medical - Scottburgh Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
